// "Active stocking date" — the day routes are or will be running.
// Time-based rule per ops workflow: before the cutoff hour we're still
// managing today's routes; after the cutoff we're prepping tomorrow's.
// Both chips operate on this date for the routes fetch. The Yesterday
// chip additionally pulls the previous day's missed stops for review.
export const ACTIVE_DATE_CUTOFF_HOUR = 16; // 4 pm local

export function getActiveStockingDate(now = new Date()) {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  if (now.getHours() >= ACTIVE_DATE_CUTOFF_HOUR) {
    d.setDate(d.getDate() + 1);
  }
  return d;
}

export function getDayBefore(date) {
  const d = new Date(date);
  d.setDate(d.getDate() - 1);
  return d;
}

// Phase constants for the chip strip. The chip labels are operational
// ("DAY OF" / "PREP"), not calendar ("today" / "yesterday"), because the
// active stocking date follows the 4pm cutoff and can diverge from the
// supervisor's wall-clock date.
export const PHASE_DAY_OF = 'day-of';
export const PHASE_PREP = 'prep';

// `SupervisorActionReviewPhase` enum values. The chip phases above are UI
// ids and differ from the wire value for day-of, so never send them as-is.
export const REVIEW_PHASE_DAY_OF = 'day_of';
export const REVIEW_PHASE_PREP = 'prep';

export function toReviewPhase(phase) {
  return phase === PHASE_DAY_OF ? REVIEW_PHASE_DAY_OF : REVIEW_PHASE_PREP;
}

// Short, compact format for the toggle chips. Example: "Jun 2".
export function formatChipDate(date) {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

// Returns minutes-since-midnight, or null for malformed input.
export function parseTimeToMinutes(hhmm) {
  if (typeof hhmm !== 'string') return null;
  const match = hhmm.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const h = Number.parseInt(match[1], 10);
  const m = Number.parseInt(match[2], 10);
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return h * 60 + m;
}

// Normalize a clock string to TimeField's "HH:MM" 24-hour option format.
// Accepts the formats we see across the codebase + BE:
//   - "9:00 am" / "9:00 AM" / "9:00am" / "9 am" / "9pm"      (OpsOrders)
//   - "09:00"                                                  (TimeField)
//   - "09:00:00" / "09:00:00.000"                              (defensive)
// Returns '' for unparseable input so the modal falls back to its placeholder.
export function normalizeClockTo24h(value) {
  if (!value || typeof value !== 'string') return '';
  const trimmed = value.trim().toLowerCase();
  if (/^\d{2}:\d{2}$/.test(trimmed)) return trimmed;
  const hhmmss = trimmed.match(/^(\d{2}):(\d{2}):\d{2}/);
  if (hhmmss) return `${hhmmss[1]}:${hhmmss[2]}`;
  const m = trimmed.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/);
  if (!m) return '';
  let hours = Number.parseInt(m[1], 10);
  const minutes = m[2] ? Number.parseInt(m[2], 10) : 0;
  const meridiem = m[3];
  if (meridiem === 'pm' && hours < 12) hours += 12;
  else if (meridiem === 'am' && hours === 12) hours = 0;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return '';
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
    2,
    '0',
  )}`;
}

// Snap an "HH:MM" string to the nearest 15-minute slot so it matches one of
// TimeField's option values. Used when prefilling PinStopModal from a
// stop's existing delivery window: NextMV's calculated times can land on
// any minute (e.g. "11:17"), but the picker only offers 15-min increments.
// Returns the value unchanged when it can't be parsed, so non-HH:MM input
// (already-snapped, empty, OpsOrders strings) flows through untouched.
export function snapToTimeSlot(hhmm) {
  if (typeof hhmm !== 'string') return hhmm || '';
  const match = hhmm.match(/^(\d{2}):(\d{2})$/);
  if (!match) return hhmm;
  const h = Number.parseInt(match[1], 10);
  const m = Number.parseInt(match[2], 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const rounded = Math.round(m / 15) * 15;
  if (rounded === 60) {
    const nextHour = (h + 1) % 24;
    return `${String(nextHour).padStart(2, '0')}:00`;
  }
  return `${String(h).padStart(2, '0')}:${String(rounded).padStart(2, '0')}`;
}

// Estimated - expected. Returns null if either is unparseable.
export function computeDelayMinutes(expectedHHMM, estimatedHHMM) {
  const expected = parseTimeToMinutes(expectedHHMM);
  const estimated = parseTimeToMinutes(estimatedHHMM);
  if (expected === null || estimated === null) return null;
  return estimated - expected;
}

// Stable per-stop key for use as a React key and as a map key. Canonical
// across the SCT-634/635 surfaces (route cards, missed-stop review,
// consolidation modal) — date is included so the same key disambiguates
// stops across days (missed stops from yesterday + today's routes can
// coexist in one view). Defensive on missing fields so partial test
// fixtures still produce stable keys.
export function getStopRowKey(stop) {
  if (!stop) return '';
  return `${stop.stockingDate || ''}#${stop.id || ''}#${
    stop.stockingIndex ?? ''
  }#${stop.stop || ''}`;
}

// Group a flat stop list (as returned by GetLocationRoutes) by route name.
// Each output entry has the route name and its stops sorted by
// stockingIndex. Empty/missing route names are filtered out.
export function groupStopsByRoute(stops) {
  const map = new Map();
  (stops || []).forEach((stop) => {
    if (!stop || !stop.route) return;
    if (!map.has(stop.route)) {
      map.set(stop.route, []);
    }
    map.get(stop.route).push(stop);
  });
  return [...map.entries()]
    .map(([routeName, list]) => ({
      routeName,
      stops: [...list].sort(
        (a, b) => (a.stockingIndex || 0) - (b.stockingIndex || 0),
      ),
    }))
    .sort((a, b) => a.routeName.localeCompare(b.routeName));
}

// Single namespace for every per-user workspace localStorage key:
//   - route-ops:checklist:<date>:<sectionId> — checklist field drafts
//   - route-ops:dispatched:<date>             — dispatch confirmation flag
//   - route-ops:route-edits:<date>:<route>    — per-route edit drafts
// Anything new that holds per-user state must live under this prefix so
// `clearAllWorkspaceState` wipes it on logout. Filter persistence
// (`supervisor-dashboard:filters`) intentionally lives outside this
// namespace and survives logout.
export const WORKSPACE_STORAGE_PREFIX = 'route-ops:';

// Read a single field from a Headcount section's localStorage draft (the
// supervisor's reported driver/van counts and reasons). Returns `''` if the
// key isn't set, the JSON is malformed, or the field is missing — callers
// can treat the result as "not reported yet" without extra null-checks.
//
// `phase` is 'day-of' (default) or 'prep'; the consolidation modal reads
// 'day-of' since headcount confirmation happens at execution time.
export function readHeadcountField(
  stockingDate,
  fieldName,
  { phase = 'day-of', storage = window.localStorage } = {},
) {
  if (!stockingDate || !fieldName) return '';
  const key = `${WORKSPACE_STORAGE_PREFIX}checklist:${stockingDate}:headcount-${phase}`;
  try {
    const raw = storage.getItem(key);
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    const value = parsed?.fields?.[fieldName];
    return value === undefined || value === null ? '' : value;
  } catch (e) {
    return '';
  }
}

// Wipes every workspace localStorage entry. Called on user logout so
// drafts don't leak to the next user on a shared workstation.
export function clearAllWorkspaceState(storage = window.localStorage) {
  const toDelete = [];
  for (let i = 0; i < storage.length; i += 1) {
    const k = storage.key(i);
    if (k && k.startsWith(WORKSPACE_STORAGE_PREFIX)) toDelete.push(k);
  }
  toDelete.forEach((k) => storage.removeItem(k));
}

// Human-friendly: "On time", "23 min late", "1h late", "1h 5m late",
// "10 min early". `null` becomes "—".
export function formatDelayMinutes(minutes) {
  if (minutes === null || minutes === undefined) return '—';
  if (minutes === 0) return 'On time';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const suffix = minutes > 0 ? 'late' : 'early';
  if (h === 0) return `${m} min ${suffix}`;
  if (m === 0) return `${h}h ${suffix}`;
  return `${h}h ${m}m ${suffix}`;
}

// Orders stops by a displayedSequence of row keys — the same derivation
// StopList applies internally (useRouteEdits owns the sequence). Stops
// absent from the sequence are dropped, matching the list's behavior.
export function orderStopsBySequence(stops, displayedSequence) {
  const byKey = new Map();
  (stops || []).forEach((s) => byKey.set(getStopRowKey(s), s));
  return (displayedSequence || []).map((key) => byKey.get(key)).filter(Boolean);
}
